/**
 * Public landing page (`/`).
 *
 * Rendered OUTSIDE the authenticated `AppShell`, the owner console and the
 * patient shells — a self-contained page with its own top nav and footer. It
 * reads the auth context only to swap the primary call-to-action between
 * "Sign in" and "Enter console" when a session already exists; it never
 * redirects, so a signed-in user can still read it.
 *
 * Styling follows the app's own system: the shadcn-style HSL tokens in
 * `index.css` (teal `--primary`, `card`, `muted`, `border`), Inter, and the
 * `Button`/`Badge` primitives. Copy and lists live in `home-data.ts`.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactNode, RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowRight, Building2, ChevronDown, Download, LogIn, MapPin, Menu, MonitorCheck, ShieldCheck, X } from 'lucide-react';
import type { PublicLandingDto, SessionUser } from '@his/shared';
import { BRANDING } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Badge, Button } from '@/components/ui/primitives';
import {
  HERO_ACTIONS,
  HERO_STATS,
  MODULE_COUNT,
  MODULE_GROUPS,
  MODULE_ICONS,
  PLATFORM_POINTS,
  SIGN_IN_SURFACES,
  TODAY_META,
  WORKFLOW_STEPS,
  type ModuleGroup,
} from './home-data';
import { usePublicHome, usePublicLanding, type PublicHome, type PublicHomeHospital } from './home-api';
import { CountUp, DotGrid, Reveal, useInView } from './home-visuals';
import { CheckVersionSection, DownloadSection } from './HomeDownloadSections';
import { TOUR_TEXT, useTourLanguage } from './tour/tour-language';
import { TOUR_SCENE_PARAM } from './tour/tour-link';
import { TourLanguageSwitch } from './tour/TourLanguageSwitch';
import { ThemeToggle } from '@/components/ThemeToggle';

const ProductTour = lazy(() => import('./tour/ProductTour'));

/* ------------------------------- small helpers ---------------------------- */

/** What every section receives; most use a subset. Keeps `SECTIONS` uniform. */
interface SectionProps {
  user: SessionUser | null;
  home: PublicHome | undefined;
  loading: boolean;
  /** Offline distribution: the latest published packages (`/public/landing`). */
  landing: PublicLandingDto | undefined;
  landingLoading: boolean;
}

function isHiddenOwnerSession(user: SessionUser): boolean {
  return user.isPlatformUser && user.platformRole !== 'contact_person';
}

/** Where "Enter console" goes for an already signed-in user. */
function consoleHomeFor(user: SessionUser): string {
  if (user.requires2fa && !user.has2fa) return '/two-factor';
  if (user.isPlatformUser) return '/client';
  return '/dashboard';
}

function scrollToId(id: string): (e: MouseEvent) => void {
  return (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

const compact = new Intl.NumberFormat('en-PK', { notation: 'compact', maximumFractionDigits: 1 });
const formatCompact = (n: number) => (n >= 10_000 ? compact.format(n) : n.toLocaleString('en-PK'));

/** Two-letter monogram from a hospital name — no external logos on a public page. */
function monogram(name: string): string {
  const words = name.replace(/[^A-Za-z\s]/g, '').trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || 'H';
}

/** Full literal class strings per group so Tailwind's scanner keeps them. */
const GROUP_TONE: Record<ModuleGroup, string> = {
  core: 'bg-muted text-foreground',
  clinical: 'bg-primary/10 text-primary',
  diagnostics: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  commerce: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  admin: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
  patient: 'bg-success/10 text-success',
};

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
      <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
      {children}
    </p>
  );
}

function SectionHeading({
  eyebrow,
  title,
  lead,
  center = false,
}: {
  eyebrow: string;
  title: ReactNode;
  lead?: string;
  center?: boolean;
}) {
  return (
    <div className={cn('max-w-2xl', center && 'mx-auto text-center')}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
      {lead && <p className="mt-4 text-base leading-7 text-muted-foreground">{lead}</p>}
    </div>
  );
}

function BrandMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary text-[11px] font-bold text-primary-foreground',
        className,
      )}
      aria-hidden
    >
      {BRANDING.productName}
    </span>
  );
}

/** The primary CTA, shared by the nav, the hero and the closing block. */
function PrimaryCta({ user, size = 'default' }: { user: SessionUser | null; size?: 'default' | 'lg' }) {
  return user && !isHiddenOwnerSession(user) ? (
    <Link to={consoleHomeFor(user)}>
      <Button size={size}>
        {HERO_ACTIONS.enterConsole} <ArrowRight className="h-4 w-4" aria-hidden />
      </Button>
    </Link>
  ) : (
    <Link to="/login">
      <Button size={size}>
        <LogIn className="h-4 w-4" aria-hidden /> {HERO_ACTIONS.signIn}
      </Button>
    </Link>
  );
}

/* ---------------------------------- top nav ------------------------------- */

const PRIMARY_LINKS = [
  { id: 'modules', label: 'Modules' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'tour', label: 'Tour' },
  { id: 'platform', label: 'Platform' },
  { id: 'hospitals', label: 'Hospitals' },
] as const;

const MORE_LINKS = [
  { id: 'download', label: 'Offline installer', icon: Download },
  { id: 'check-version', label: 'Check my version', icon: MonitorCheck },
] as const;

const NAV_LINKS = [...PRIMARY_LINKS, ...MORE_LINKS];

const NAV_LINK_CLASS =
  'whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-ring';

function MoreLinksMenu() {
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger className={cn(NAV_LINK_CLASS, 'group inline-flex items-center gap-1 data-[state=open]:bg-accent')}>
        More
        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className={cn(
            'z-50 min-w-[13rem] rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-1',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          {MORE_LINKS.map(({ id, label, icon: Icon }) => (
            <DropdownMenu.Item key={id} asChild>
              <a
                href={`#${id}`}
                onClick={scrollToId(id)}
                className="flex cursor-pointer select-none items-center gap-2.5 rounded-sm px-2.5 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
              >
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                {label}
              </a>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function useDismissibleMenu(open: boolean, close: () => void, container: RefObject<HTMLElement>) {
  useEffect(() => {
    if (!open) return;
    const wide = window.matchMedia('(min-width: 1024px)');
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) close();
    };
    const onWidth = () => {
      if (wide.matches) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    wide.addEventListener('change', onWidth);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      wide.removeEventListener('change', onWidth);
    };
  }, [open, close, container]);
}

function TopNav({ user }: { user: SessionUser | null }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useDismissibleMenu(menuOpen, closeMenu, headerRef);

  const followLink = (id: string) => (event: MouseEvent) => {
    scrollToId(id)(event);
    closeMenu();
  };

  return (
    <header ref={headerRef} className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
        <a href="#top" onClick={followLink('top')} className="flex shrink-0 items-center gap-2.5 rounded-md focus-ring">
          <BrandMark />
          <span className="hidden whitespace-nowrap text-sm font-semibold tracking-tight sm:inline lg:hidden xl:inline">
            {BRANDING.productFullName}
          </span>
        </a>

        <nav className="mx-auto hidden items-center gap-0.5 lg:flex" aria-label="Sections">
          {PRIMARY_LINKS.map((link) => (
            <a key={link.id} href={`#${link.id}`} onClick={scrollToId(link.id)} className={NAV_LINK_CLASS}>
              {link.label}
            </a>
          ))}
          <MoreLinksMenu />
        </nav>

        <div className="ms-auto flex shrink-0 items-center gap-1.5 lg:ms-0">
          <Link to="/find" className={cn(NAV_LINK_CLASS, 'hidden items-center gap-1.5 lg:inline-flex')}>
            <MapPin className="h-4 w-4" aria-hidden />
            Find care
          </Link>
          <ThemeToggle />
          <PrimaryCta user={user} />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="home-mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {menuOpen && (
        <div
          id="home-mobile-nav"
          className="absolute inset-x-0 top-full max-h-[calc(100dvh-4rem)] overflow-y-auto border-b bg-background shadow-lg duration-200 animate-in fade-in-0 slide-in-from-top-2 lg:hidden"
        >
          <nav className="mx-auto grid max-w-6xl gap-1 px-4 py-3 sm:grid-cols-2 sm:px-6" aria-label="Sections">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={followLink(link.id)}
                className="rounded-md px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-ring"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/find"
              onClick={closeMenu}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-accent focus-ring"
            >
              <MapPin className="h-4 w-4" aria-hidden />
              Find care
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ----------------------------------- hero --------------------------------- */

function ConsolePreview({ home }: { home: PublicHome | undefined }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-lg">
      <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <BrandMark className="h-6 w-6 rounded text-[9px]" />
          <span className="text-xs font-medium">Across the platform</span>
        </div>
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className={cn('h-1.5 w-1.5 rounded-full', home ? 'bg-success' : 'bg-muted-foreground/40')} aria-hidden />
          {home ? 'Live' : 'Connecting'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-px bg-border">
        {TODAY_META.map((meta) => (
          <div key={meta.key} className="bg-card p-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{meta.label}</p>
            <p className="mt-1 text-2xl font-semibold tabular">
              {home ? <CountUp value={home.stats[meta.key]} /> : <span className="text-muted-foreground">—</span>}
            </p>
          </div>
        ))}
      </div>

      <div className="border-t px-4 py-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Licensed per hospital</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {MODULE_GROUPS.flatMap((g) => g.modules)
            .slice(0, 12)
            .map((m) => {
              const Icon = MODULE_ICONS[m.code];
              return (
                <span
                  key={m.code}
                  className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-[11px] text-muted-foreground"
                >
                  <Icon className="h-3 w-3 text-primary" aria-hidden />
                  {m.name.replace(/\s*\(.*\)$/, '')}
                </span>
              );
            })}
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] text-muted-foreground">
            +{MODULE_COUNT - 12} more
          </span>
        </div>
      </div>
    </div>
  );
}

function Hero({ user, home }: { user: SessionUser | null; home: PublicHome | undefined }) {
  return (
    <section id="top" className="relative overflow-hidden border-b">
      <DotGrid className="pointer-events-none absolute inset-0" />
      <div
        aria-hidden
        className="pointer-events-none absolute -end-32 -top-32 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:pb-24 lg:pt-20">
        <div>
          <Badge variant="default" className="gap-1.5">
            <Building2 className="h-3 w-3" aria-hidden />
            Multi-tenant hospital information system
          </Badge>

          <h1 className="mt-5 text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl">
            {BRANDING.tagline}
          </h1>

          <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground">{BRANDING.shortDescription}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <PrimaryCta user={user} size="lg" />
            <a href="#download" onClick={scrollToId('download')}>
              <Button variant="outline" size="lg">
                <Download className="h-4 w-4 text-primary" aria-hidden /> {HERO_ACTIONS.download}
              </Button>
            </a>
            <a href="#check-version" onClick={scrollToId('check-version')}>
              <Button variant="ghost" size="lg">
                <MonitorCheck className="h-4 w-4 text-primary" aria-hidden /> {HERO_ACTIONS.checkVersion}
              </Button>
            </a>
          </div>

          <dl className="mt-12 grid max-w-lg grid-cols-2 gap-6 sm:grid-cols-4">
            {HERO_STATS.map((stat, i) => (
              <Reveal key={stat.key} delay={i * 80}>
                <dt className="text-2xl font-semibold tabular">
                  {home ? (
                    <CountUp value={home.stats[stat.key]} format={formatCompact} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </dt>
                <dd className="mt-1 text-xs text-muted-foreground">{stat.label}</dd>
              </Reveal>
            ))}
          </dl>
        </div>

        <Reveal delay={120}>
          <ConsolePreview home={home} />
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------- modules -------------------------------- */

function ModulesSection(_props: SectionProps) {
  return (
    <section id="modules" className="scroll-mt-16 py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Modules"
          title={`${MODULE_COUNT} modules, licensed one hospital at a time`}
          lead="Every module in the SRS is built and switched on per tenant. A dependency engine keeps the combination sane — a ward cannot be sold without billing, a theatre not without a ward."
        />

        <div className="mt-12 space-y-12">
          {MODULE_GROUPS.map((group, gi) => (
            <Reveal key={group.group} delay={gi * 40}>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h3 className="text-lg font-semibold">{group.title}</h3>
                <p className="text-sm text-muted-foreground">{group.blurb}</p>
              </div>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.modules.map((m) => {
                  const Icon = MODULE_ICONS[m.code];
                  return (
                    <li key={m.code} className="flex gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-primary/40">
                      <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-md', GROUP_TONE[m.group])}>
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{m.name}</p>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">{m.description}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- workflow ------------------------------- */

function WorkflowSection(_props: SectionProps) {
  return (
    <section id="workflow" className="scroll-mt-16 border-t bg-muted/30 py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Workflow"
          title="One patient record from the front desk to discharge"
          lead="Each department works its own screen, but the encounter, the orders, the stock and the bill are one chain of records — nothing is re-typed between them."
        />

        <ol className="relative mt-12 grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          <div aria-hidden className="absolute start-0 end-0 top-6 hidden h-px bg-border lg:block" />
          {WORKFLOW_STEPS.map((step, i) => (
            <Reveal key={step.index} delay={i * 70}>
              <li className="relative h-full rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground">
                    <step.icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="text-xs font-semibold tabular text-muted-foreground">{step.index}</span>
                </div>
                <p className="mt-3 text-sm font-medium">{step.title}</p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{step.description}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ----------------------------------- tour --------------------------------- */

function TourSection(_props: SectionProps) {
  const [ref, near] = useInView<HTMLDivElement>('0px 0px 700px 0px');
  const [language, setLanguage] = useTourLanguage();
  const [params] = useSearchParams();
  const deepLinked = Boolean(params.get(TOUR_SCENE_PARAM));
  const text = TOUR_TEXT[language];
  useEffect(() => {
    if (!deepLinked) return;
    const timer = window.setTimeout(() => document.getElementById('tour')?.scrollIntoView({ block: 'start' }), 50);
    return () => window.clearTimeout(timer);
  }, [deepLinked]);
  return (
    <section id="tour" className="scroll-mt-16 border-t py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading eyebrow={text.eyebrow} title={text.title} lead={text.lead} />
          <TourLanguageSwitch language={language} onChange={setLanguage} />
        </div>
        <div ref={ref} className="mt-10 min-h-[820px]">
          {near || deepLinked ? (
            <Suspense fallback={<div className="h-[820px] animate-pulse rounded-xl border bg-muted/40" />}>
              <ProductTour language={language} />
            </Suspense>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- platform ------------------------------- */

function PlatformSection(_props: SectionProps) {
  return (
    <section id="platform" className="scroll-mt-16 border-t py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Platform"
          title="Built so the invariants hold, not just the happy path"
          lead="The rules the SRS calls non-negotiable are enforced in the database and the guards, where a forgotten line of application code cannot switch them off."
        />

        <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PLATFORM_POINTS.map((point, i) => (
            <Reveal key={point.title} delay={i * 60}>
              <li className="h-full rounded-lg border bg-card p-5">
                <span className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <point.icon className="h-4 w-4" aria-hidden />
                </span>
                <p className="mt-4 text-sm font-semibold">{point.title}</p>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{point.description}</p>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------------------------- stats --------------------------------- */

const STAT_ROW: readonly { key: keyof PublicHome['stats']; label: string }[] = [
  { key: 'branches', label: 'Branches' },
  { key: 'emergencyCases', label: 'Emergency cases' },
  { key: 'surgeries', label: 'Surgeries scheduled' },
  { key: 'labOrders', label: 'Lab orders' },
  { key: 'radiologyStudies', label: 'Radiology studies' },
  { key: 'invoices', label: 'Invoices issued' },
];

function StatsRow({ home }: SectionProps) {
  return (
    <section aria-label="Platform statistics" className="border-t bg-primary text-primary-foreground">
      <dl className="mx-auto grid max-w-6xl grid-cols-2 gap-x-6 gap-y-8 px-4 py-12 sm:grid-cols-3 sm:px-6 lg:grid-cols-6">
        {STAT_ROW.map((stat, i) => (
          <Reveal key={stat.key} delay={i * 50}>
            <dt className="text-3xl font-semibold tabular">
              {home ? <CountUp value={home.stats[stat.key]} format={formatCompact} /> : '—'}
            </dt>
            <dd className="mt-1 text-xs text-primary-foreground/80">{stat.label}</dd>
          </Reveal>
        ))}
      </dl>
    </section>
  );
}

/* -------------------------------- hospitals ------------------------------- */

function HospitalCard({ hospital }: { hospital: PublicHomeHospital }) {
  const since = hospital.since ? new Date(hospital.since).getFullYear() : null;
  return (
    <li>
      <Link
        to={`/find/${hospital.slug}`}
        className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent focus-ring"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
          {monogram(hospital.name)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{hospital.name}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
            {hospital.city ? (
              <>
                <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                {hospital.city}
              </>
            ) : (
              'Pakistan'
            )}
            {since && <span className="ms-1 text-muted-foreground/70">· since {since}</span>}
          </p>
        </div>
      </Link>
    </li>
  );
}

const FACILITY_GROUPS = [
  {
    type: 'hospital' as const,
    heading: 'Hospitals and clinics',
    blurb: 'Book an appointment, see a doctor, and reach your reports.',
  },
  {
    type: 'lab' as const,
    heading: 'Laboratories',
    blurb: 'Book a test, and collect the report when it is released.',
  },
  {
    type: 'pharmacy' as const,
    heading: 'Medical stores',
    blurb: 'Order medicines and collect them from the counter.',
  },
];

function HospitalsSection({ home, loading }: SectionProps) {
  const hospitals = home?.hospitals ?? [];
  return (
    <section id="hospitals" className="scroll-mt-16 border-t py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Who is on it"
          title={`On ${BRANDING.productName}`}
          lead="Hospitals, laboratories and medical stores, each running in its own tenant on one deployment. Open any of them to book an appointment, a test, or an order."
        />

        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading the roster…</p>
        ) : hospitals.length === 0 ? (
          <p className="mt-10 text-sm text-muted-foreground">Nothing is listed publicly yet.</p>
        ) : (
          <div className="mt-10 space-y-10">
            {FACILITY_GROUPS.map((group) => {
              const inGroup = hospitals.filter((h) => h.facilityType === group.type);
              if (inGroup.length === 0) return null;
              return (
                <div key={group.type}>
                  <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="text-lg font-semibold">{group.heading}</h3>
                    <span className="text-xs text-muted-foreground">{inGroup.length}</span>
                    <p className="w-full text-sm text-muted-foreground sm:w-auto">{group.blurb}</p>
                  </div>
                  <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {inGroup.map((hospital, i) => (
                      <Reveal key={hospital.id} delay={i * 40}>
                        <HospitalCard hospital={hospital} />
                      </Reveal>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-xs text-muted-foreground">
          Looking for somewhere by city or by the test you need? Search the{' '}
          <Link to="/find" className="font-medium text-primary underline-offset-4 hover:underline">
            public directory
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

/* --------------------------------- downloads ------------------------------ */

/**
 * Offline distribution (plan §3.2, §3.7): the installer download, gated by
 * hospital code + PIN, and the "Check my version" probe of a hospital's own
 * server. Both live in `HomeDownloadSections.tsx`; this keeps the slot in
 * `SECTIONS` (after the hospitals roster, before the CTA).
 */
function DownloadsSection({ landing, landingLoading }: SectionProps) {
  return (
    <>
      <DownloadSection landing={landing} loading={landingLoading} />
      <CheckVersionSection landing={landing} />
    </>
  );
}

/* ----------------------------------- cta ---------------------------------- */

function CtaSection({ user }: SectionProps) {
  const signedIn = user && !isHiddenOwnerSession(user);
  return (
    <section className="border-t bg-muted/30 py-20 sm:py-24">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {signedIn ? 'Your console is one click away.' : 'Staff sign in here.'}
        </h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">
          {signedIn
            ? 'You are already signed in — pick up where you left off.'
            : 'Hospital staff sign in with their hospital, email and password; patients use the portal.'}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCta user={user} size="lg" />
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- footer -------------------------------- */

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col justify-between gap-10 md:flex-row">
          <div className="max-w-xs">
            <div className="flex items-center gap-2.5">
              <BrandMark />
              <span className="text-sm font-semibold tracking-tight">{BRANDING.productFullName}</span>
            </div>
            <p className="mt-3 text-xs leading-5 text-muted-foreground">{BRANDING.shortDescription}</p>
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Product</p>
              <ul className="mt-3 space-y-2">
                {NAV_LINKS.map((link) => (
                  <li key={link.id}>
                    <a
                      href={`#${link.id}`}
                      onClick={scrollToId(link.id)}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sign in</p>
              <ul className="mt-3 space-y-2">
                {SIGN_IN_SURFACES.map((surface) => (
                  <li key={surface.to}>
                    <Link
                      to={surface.to}
                      title={surface.description}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {surface.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contact</p>
              <ul className="mt-3 space-y-2">
                <li>
                  <a
                    href={`mailto:${BRANDING.contactEmail}`}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {BRANDING.contactEmail}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t pt-6 sm:flex-row">
          <p className="text-xs text-muted-foreground">
            © {year} {BRANDING.productFullName}
          </p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
            Multi-tenant · RBAC · audited · licensed per hospital
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------------- page --------------------------------- */

/**
 * Page order. Kept as data so a later section (the downloads slot above) is
 * inserted by adding a line here, not by re-reading the JSX below.
 */
const SECTIONS = [
  ModulesSection,
  WorkflowSection,
  TourSection,
  PlatformSection,
  StatsRow,
  HospitalsSection,
  DownloadsSection,
  CtaSection,
] as const;

export function HomePage() {
  const { user, loading } = useAuth();
  const home = usePublicHome();
  const landing = usePublicLanding();
  // While the session is still being confirmed the CTA reads "Sign in"; it
  // flips to "Enter console" a moment later rather than flashing a redirect.
  const sessionUser = loading ? null : user;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav user={sessionUser} />
      <main>
        <Hero user={sessionUser} home={home.data} />
        {SECTIONS.map((Section) => (
          <Section
            key={Section.name}
            user={sessionUser}
            home={home.data}
            loading={home.isLoading}

            landing={landing.data}
            landingLoading={landing.isLoading}
          />
        ))}
      </main>
      <Footer />
    </div>
  );
}
